import { useTheme } from '@emotion/react';
import { Box, Grid, Typography, Button } from '@mui/material';
import { Link, NavLink } from 'react-router-dom';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import ComingSoonImageSrc from 'src/assets/png/rebookers-coming-soon-large.png';
import LoveItDontLoseItImageSrc from 'src/assets/png/rebookers-pricing-released-lock-up.png';
import { ReactComponent as IconArrow } from 'src/assets/svg/control-arrow.svg';
import RebookersLockupSrc from 'src/assets/png/rebook-lockup-cropped.png';

import { routes } from '../../../../constants/routes';
import { RebookStatus } from '../../../rebooking/api/constants';
import RebookingHeader from '../../../rebooking/assets/hos_rebooking_2728_white.png';
import { getTheme } from '../../../../utils/get-theme';

type RebookingBannerProps = {
    className?: string;
    rebookStatus: RebookStatus;
};

export const RebookingBanner: FC<RebookingBannerProps> = ({ className, rebookStatus }) => {
    const { t } = useTranslation();
    const { palette } = useTheme();
    const { isHosTheme } = getTheme();

    const canGoToRebookersPage =
        rebookStatus === RebookStatus.Active || rebookStatus === RebookStatus.PriceReleased;

    return (
        <Box
            className={className}
            id="rebookers-banner"
            position="relative"
            css={{
                backgroundImage: `url("${ComingSoonImageSrc}")`,
                backgroundRepeat: 'no-repeat',
                backgroundOrigin: 'border-box',
                backgroundPosition: 'center',
                backgroundSize: 'cover',
                textDecoration: 'none',

                ':hover': {
                    cursor: 'pointer',
                },
            }}
            {...(canGoToRebookersPage && {
                component: NavLink,
                to: routes.rebooking.index,
            })}
        >
            <Grid
                padding={4}
                container
                css={{
                    height: '100%',
                    width: '100%',
                    background:
                        'linear-gradient(0deg,rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.2) 80%, rgba(0, 0, 0, 0) 100%);',
                }}
                justifyContent="space-between"
            >
                <Grid item xs={12} md={7}>
                    <Grid container direction="column" justifyContent="space-between" height="100%">
                        <Grid
                            item
                            xs="auto"
                            display="flex"
                            justifyContent="flex-start"
                            alignItems="flex-start"
                            maxHeight="50%"
                        >
                            <img
                                alt="Rebook CTA"
                                src={isHosTheme ? RebookingHeader : RebookersLockupSrc}
                                css={{ objectFit: 'contain', maxWidth: '77%' }}
                            />
                        </Grid>
                        <Grid item xs="auto" md={5}>
                            <Typography variant="h5" color={palette.grey[50]} mb={1}>
                                {t('rebooking_banner-title')}
                            </Typography>
                            <Button
                                {...(canGoToRebookersPage && {
                                    component: Link,
                                    to: routes.rebooking.index,
                                })}
                                css={{
                                    borderBottom: '1px solid white',
                                    borderRadius: 0,
                                    color: 'white',
                                    ...(canGoToRebookersPage && {
                                        ':hover': {
                                            color: palette.primary.main,
                                            borderBottomColor: palette.primary.main,
                                            backgroundColor: 'transparent',
                                        },
                                    }),
                                }}
                                endIcon={
                                    canGoToRebookersPage ? (
                                        <IconArrow
                                            css={{
                                                transform: 'rotate(-90deg)',
                                            }}
                                        />
                                    ) : null
                                }
                            >
                                <Typography fontWeight={700}>
                                    {canGoToRebookersPage
                                        ? t('rebooking_banner-cta')
                                        : 'Coming Soon'}
                                </Typography>
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
                {!isHosTheme && (
                    <Grid
                        item
                        xs={3}
                        md={5}
                        display={{ xs: 'none', md: 'flex' }}
                        justifyContent="flex-end"
                        alignItems="flex-start"
                    >
                        <img
                            alt="Rebook CTA 2"
                            src={LoveItDontLoseItImageSrc}
                            css={{ objectFit: 'contain', maxWidth: '66%' }}
                        />
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};
